/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-key */
import get from 'lodash/get'
import React, { FC, ReactElement, useMemo } from 'react'
import { useTable } from 'react-table'
import Skeleton from '../skeleton'
import { Container } from './styles'

type Column = {
  position?: 'right' | 'left' | 'center'
  title: string
  dataIndex: string
  render?: (value: any, data: any) => ReactElement | string | null
}

type TableProps = {
  columns: Column[]
  data?: any[]
  loading?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

type PositionElProps = {
  children: ReactElement | string | null
  position?: 'left' | 'right' | 'center'
}

const NoData: FC<{ columns: any[] }> = ({ columns }) => {
  return (
    <tr>
      <td colSpan={columns.length}>
        <div
          style={{
            paddingTop: 20,
            color: 'rgba(0,0,0,0.2)',
            textAlign: 'center'
          }}
        >
          Não há dados
        </div>
      </td>
    </tr>
  )
}

const PositionEl: FC<PositionElProps> = ({ position, children }): ReactElement => {
  if (position === 'right') { return <div style={{ textAlign: 'right' }}>{children}</div> }
  if (position === 'center') { return <div style={{ textAlign: 'center' }}>{children}</div> }

  return <>{children}</>
}

const Table: FC<TableProps> = ({
  columns: _columns,
  data: _data = [],
  loading,
  className,
  size = 'md'
}): ReactElement => {
  const columns = useMemo(() => {
    return _columns.map((d) => {
      return {
        Header: <PositionEl position={d.position}>{d.title}</PositionEl>,
        accessor: d.dataIndex
      }
    })
  }, [_columns])

  const data = useMemo(() => {
    if (loading) {
      const loadingData = Array.from(Array(10)).map((_, i) => {
        return _columns.reduce((acc, { dataIndex }) => ({
          ...acc,
          [dataIndex]: <Skeleton key={`table-${i}-${dataIndex}`}>Loading...</Skeleton>
        }), {})
      })

      return loadingData
    }

    return _data.map((d) => {
      return Object.keys(d).reduce((acc, key) => {
        const column = _columns.find((c) => c.dataIndex === key)
        if (column?.render) {
          return {
            ...acc,
            [key]: (
              <PositionEl position={column?.position}>
                {column.render(get(d, key), d)}
              </PositionEl>
            )
          }
        }

        return {
          ...acc,
          [key]: (
            <PositionEl position={column?.position}>{get(d, key)}</PositionEl>
          )
        }
      }, {})
    })
  }, [loading, _data, _columns])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({ columns, data })

  return (
    <Container size={size}>
      <table className={className} {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {data.length === 0 && <NoData columns={columns} />}
          {rows.map((row) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </Container>
  )
}

export default Table
